/**
 * @file AssetHistoryPanel.js
 * @description This panel populates this history events associated with an asset.
 */

import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import FullscreenDisplay from '../shared/FullscreenDisplay';
import { getFileCategory, processFileName } from '../../helpers/FileUtils';
import { formatDateToMonthDDYYYYTimezone } from '../../helpers/DateUtils';
import styles from '../AssetDrawer.module.scss';
import { getText } from '../../../i18n';
import { ResourceScope } from '../../../utils/data/enums';

const AssetHistoryPanel = ({ asset }) => {
    const [isOpen, setIsOpen] = useState(null);
    const [offsets, setOffsets] = useState([]);
    const [barHeight, setBarHeight] = useState(0);
    const historyRefs = useRef([]);
    const [sortedHistories, setSortedHistories] = useState([]);

    useEffect(() => {
        if (asset) {
            const assetHistories = Array.isArray(asset.assetHistories)
                ? asset.assetHistories
                : [];
            let sortedHistoriesArray = [...assetHistories];
            sortedHistoriesArray.push({
                name: asset.name,
                description: asset.description,
                tags: (asset.assetTags || []).map(label => ({
                    tag: label,
                })),
                createdAt: asset.createdAt,
                createdBy: asset.createdBy,
                updatedAt: asset.updatedAt,
                updatedBy: asset.updatedBy,
                url: asset.url,
                fileType: asset.fileType,
                resourceScopeMask: asset.resourceScopeMask,
            });
            sortedHistoriesArray.sort(
                (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
            );
            setSortedHistories(sortedHistoriesArray);
        }
    }, [asset]);

    const toggleAccordion = index => {
        setIsOpen(isOpen === index ? null : index);
    };

    const calculateOffsets = () => {
        const baseHeight = 82.06;
        const newOffsets = [0];
        for (let index = 1; index < sortedHistories.length; index++) {
            const ref = historyRefs.current[index - 1];
            const height = ref ? ref.clientHeight : 0;
            const additionalHeight = isOpen === index - 1 ? height + 9.2 : 0;
            newOffsets[index] =
                newOffsets[index - 1] + baseHeight + additionalHeight;
        }
        return newOffsets;
    };

    useEffect(() => {
        setOffsets(calculateOffsets());
        const closedHeight = (sortedHistories.length - 1) * 82.06;
        if (isOpen !== null) {
            const isLastEvent = isOpen === sortedHistories.length - 1;
            const openedHeight = isLastEvent
                ? closedHeight
                : (historyRefs.current[isOpen]?.clientHeight + 9.2 || 0) +
                  closedHeight;
            setBarHeight(openedHeight);
        } else {
            setBarHeight(closedHeight);
        }
    }, [isOpen, sortedHistories]);

    const getTimestamp = index => {
        console.log(sortedHistories);
        let date = '';
        if (index === sortedHistories.length - 1) {
            date = sortedHistories[index].createdAt;
        } else {
            date = sortedHistories[index].updatedAt;
        }
        return formatDateToMonthDDYYYYTimezone(date);
    };

    const getAuthor = index => {
        let author = 'Unknown';
        if (index === sortedHistories.length - 1) {
            author = sortedHistories[index].createdBy;
        } else {
            author = sortedHistories[index].updatedBy;
        }
        return author;
    };

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState('');

    const openFullscreen = imageUrl => {
        setFullscreenImageUrl(imageUrl);
        setIsFullscreen(true);
    };

    const closeFullscreen = () => {
        setIsFullscreen(false);
        setFullscreenImageUrl('');
    };

    const downloadAsset = asset => {
        const processedName = processFileName(asset.name, asset.fileType);

        fetch(asset.url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response failed');
                }
                return response.blob();
            })
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = processedName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(error => {
                console.error(
                    'There was a problem downloading the asset:',
                    error,
                );
            });
    };

    const getResourceScopeNames = resourceScopeMask =>
        Object.entries(ResourceScope)
            .filter(([, value]) => (resourceScopeMask & value) === value)
            .map(([name]) => (name === 'ClientAdmin' ? 'Client Admin' : name))
            .join(', ');

    return (
        <>
            <p className={styles.historyPanelTitle}>
                {getText('msg_90ccd6497400')}
            </p>
            <div className={styles.assetHistoryPanel}>
                <div
                    className={styles.verticalBar}
                    style={{ height: `${barHeight}px` }}
                >
                    {sortedHistories.map((event, index) => (
                        <div
                            key={event.id}
                            className={`${styles.circle} ${isOpen === index ? styles.activeCircle : ''}`}
                            style={{ top: `${offsets[index]}px` }}
                        />
                    ))}
                </div>
                <div className={styles.historyContent}>
                    {sortedHistories.map((event, index) => (
                        <div key={event.id} className={styles.historyEntry}>
                            <div
                                className={styles.historyHeader}
                                onClick={() => toggleAccordion(index)}
                            >
                                <div>
                                    <span className={styles.historyTimestamp}>
                                        {getTimestamp(index)}
                                    </span>
                                    <span className={styles.historyCaret}>
                                        {isOpen === index ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 15 15"
                                                fill="none"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M12.7838 9.96502C12.6169 10.1397 12.34 10.1461 12.1652 9.97921L7.11252 5.15323L2.28654 10.2059C2.11965 10.3807 1.84272 10.387 1.66799 10.2201C1.49326 10.0532 1.4869 9.7763 1.65379 9.60158L6.78195 4.2325C6.94884 4.05777 7.22578 4.05141 7.40051 4.2183L12.7696 9.34646C12.9443 9.51335 12.9507 9.79029 12.7838 9.96502Z"
                                                    fill="var(--iNGAGED-Neutral-Color2)"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="16"
                                                viewBox="0 0 15 16"
                                                fill="none"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M1.52926 5.36069C1.6959 5.18572 1.97283 5.17896 2.1478 5.3456L7.20742 10.1643L12.0261 5.10469C12.1928 4.92972 12.4697 4.92297 12.6447 5.08961C12.8196 5.25625 12.8264 5.53317 12.6598 5.70814L7.53932 11.0846C7.37268 11.2595 7.09575 11.2663 6.92078 11.0997L1.54435 5.97922C1.36938 5.81258 1.36262 5.53565 1.52926 5.36069Z"
                                                    fill="var(--iNGAGED-Neutral-Color2)"
                                                />
                                            </svg>
                                        )}
                                    </span>
                                </div>
                                <div>
                                    <span className={styles.historyAuthor}>
                                        {getAuthor(index)}
                                    </span>
                                    {(index === sortedHistories.length - 1 ||
                                        index === 0) && (
                                        <span className={styles.historyVersion}>
                                            {index ===
                                            sortedHistories.length - 1
                                                ? 'Created'
                                                : 'Current'}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div
                                ref={el => (historyRefs.current[index] = el)}
                                className={classNames(
                                    styles.historyAccordionBody,
                                    {
                                        [styles.tabPanelHidden]:
                                            isOpen !== index,
                                    },
                                )}
                            >
                                {isOpen === index && (
                                    <div className={styles.historyDetails}>
                                        {getFileCategory(asset.fileType) ===
                                        'image' ? (
                                            <div
                                                className={
                                                    styles.historyImageContainer
                                                }
                                            >
                                                <img
                                                    src={event.url}
                                                    alt={event.name}
                                                    className={
                                                        styles.historyImage
                                                    }
                                                />
                                                <span
                                                    className={
                                                        styles.activeViewMode
                                                    }
                                                    onClick={() =>
                                                        openFullscreen(
                                                            event.url,
                                                        )
                                                    }
                                                >
                                                    <i className="bi bi-fullscreen"></i>
                                                </span>
                                                <span
                                                    className={
                                                        styles.activeViewMode
                                                    }
                                                    onClick={() =>
                                                        downloadAsset(event)
                                                    }
                                                >
                                                    <i className="bi bi-cloud-download"></i>
                                                </span>
                                            </div>
                                        ) : (
                                            <div
                                                className={
                                                    styles.readOnlyHistoryItem
                                                }
                                            >
                                                <p>
                                                    {getText(
                                                        'msg_10e7ac23a20a',
                                                    )}
                                                </p>
                                                <div
                                                    className={
                                                        styles.historyFileRow
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.historyFileNameLabel
                                                        }
                                                    >
                                                        {processFileName(
                                                            event.name,
                                                            event.fileType,
                                                        )}
                                                    </span>
                                                    <span
                                                        className={
                                                            styles.activeViewMode
                                                        }
                                                        onClick={() =>
                                                            downloadAsset(event)
                                                        }
                                                    >
                                                        <i className="bi bi-cloud-download"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            className={
                                                styles.readOnlyHistoryItem
                                            }
                                        >
                                            <p>{getText('msg_709a23220f2c')}</p>
                                            <p>{event.name}</p>
                                        </div>
                                        <div
                                            className={
                                                styles.readOnlyHistoryItem
                                            }
                                        >
                                            <p>{getText('msg_1910e1df6652')}</p>
                                            <p>{event.description}</p>
                                        </div>
                                        {event.resourceScopeMask !==
                                            undefined && (
                                            <div
                                                className={
                                                    styles.readOnlyHistoryItem
                                                }
                                            >
                                                <p>
                                                    {getText(
                                                        'msg_4651a34e4df9',
                                                    )}
                                                </p>
                                                <p>
                                                    {getResourceScopeNames(
                                                        event.resourceScopeMask,
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                        <div
                                            className={
                                                styles.readOnlyHistoryItem
                                            }
                                        >
                                            <p>{getText('msg_848eed0fbd54')}</p>
                                            <div className={styles.tagsRow}>
                                                {event.tags?.map(
                                                    (tag, index) => (
                                                        <span
                                                            key={index}
                                                            className={
                                                                styles.tag
                                                            }
                                                        >
                                                            {tag.tag ?? tag}
                                                        </span>
                                                    ),
                                                )}
                                                {event.tags?.length < 1 && (
                                                    <p
                                                        className={
                                                            styles.emptyTagsText
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_b88ef5aa4d2f',
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <FullscreenDisplay
                    isFullscreen={isFullscreen}
                    fullscreenImageUrl={fullscreenImageUrl}
                    closeFullscreen={closeFullscreen}
                />
            </div>
        </>
    );
};

export default AssetHistoryPanel;
